import React, { useEffect, useState, useRef } from 'react';
import { Snack } from 'snack-sdk';
import { QRCodeSVG } from 'qrcode.react';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { Button, Box } from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';

export const SnackPreview = ({ files, dependencies }) => {
  const [url, setUrl] = useState(null);
  const [showQR, setShowQR] = useState(false);
  const snackRef = useRef(null);

  // Initialize the Snack instance once
  useEffect(() => {
    if (!snackRef.current) {
      snackRef.current = new Snack({
        sdkVersion: '52.0.0',
        dependencies: {
          '@react-navigation/stack': { version: '*' }
        },
        files: {
          'App.js': {
            type: 'CODE',
            contents: `
import * as React from 'react';
import { View, Text } from 'react-native';

export default () => (
  <View style={{flex: 1, justifyContent: 'center'}}>
    <Text style={{fontSize: 20, textAlign: 'center'}}>
      Loading
    </Text>
  </View>
);
          `
          }
        }
      });

      // Fetch the initial URL
      const fetchInitialUrl = async () => {
        try {
          snackRef.current.setOnline(true);
          const { url } = await snackRef.current.getStateAsync();
          setUrl(url);
        } catch (error) {
          console.error('Error fetching initial Snack URL:', error);
        }
      };

      fetchInitialUrl();
    }
  }, []);

  // Update Snack files and dependencies whenever `files` or `dependencies` change
  useEffect(() => {
    const updateSnack = async () => {
      if (!snackRef.current) return;

      try {
        // Step 1: Read and update files
        const contents = {};

        for (const file of files) {
          try {
            const content = await new Promise((resolve, reject) => {
              const reader = new FileReader();
              reader.onload = (event) => resolve(event.target.result);
              reader.onerror = () => reject(new Error(`Failed to read file: ${file.name}`));
              reader.readAsText(file);
            });

            contents[file.name] = {
              type: 'CODE',
              contents: content
            };
          } catch (error) {
            console.error(error);
          }
        }

        // Update the Snack with new files
        await snackRef.current.updateFiles(contents);
        //console.log('Files updated successfully.');

        // Get current state to identify missing dependencies
        const state = await snackRef.current.getStateAsync();
        const { missingDependencies } = state;
        
        // Transform user-provided dependencies array into { name: { version: "*" } }
        const depsToUpdate = {};
        if (Array.isArray(dependencies)) {
          dependencies.forEach(depName => {
            depsToUpdate[depName] = { version: "*" };
          });
        }

        // Include missing dependencies (if not already included)
        if (missingDependencies && Object.keys(missingDependencies).length > 0) {
          //console.log('Missing Dependencies:', missingDependencies);
          for (const name in missingDependencies) {
            if (!depsToUpdate[name]) {
              depsToUpdate[name] = {
                version: missingDependencies[name].wantedVersion
              };
            }
          }
        }

        // If we have any dependencies to update, update them
        if (Object.keys(depsToUpdate).length > 0) {
          await snackRef.current.updateDependencies(depsToUpdate);
          //console.log('Dependencies updated successfully.');
        }

        // Log the current dependencies
        const currentState = await snackRef.current.getStateAsync();
        //console.log('Current Snack Dependencies:', currentState.dependencies);

        // Step 3: Fetch the updated URL
        const updatedState = await snackRef.current.getStateAsync();
        setUrl(updatedState.url);
        //console.log('Snack URL updated.');
      } catch (error) {
        console.error('Error updating Snack:', error);
      }
    };

    // Attempt updates if snackRef is initialized and we have either files or dependencies
    if (snackRef.current && (files.length > 0 || (dependencies && dependencies.length > 0))) {
      updateSnack();
    }
  }, [files, dependencies]);

  const handleDownload = async () => {
    if (snackRef.current) {
      try {
        const url = await snackRef.current.getDownloadURLAsync(); // Await the promise
        const link = document.createElement('a');
        link.href = url;
        link.download = 'snack-project.zip'; // You can specify the file name
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        //console.log('Project downloaded successfully.');
      } catch (error) {
        console.error('Error downloading project:', error);
      }
    }
  };

  if (!url) return <div>Loading...</div>;

  return (
    <div style={{ position: 'relative' }}>
      <div style={{ display: 'flex'}}>
      <Box sx={{ mr: 2 }}>
      <Button
        variant="contained"
        onClick={() => setShowQR(!showQR)}
        sx={{
          backgroundColor: '#222',
          color: 'white',
          textTransform: 'none',
          '&:hover': {
            backgroundColor: '#444',
          },
        }}
      >
        <VisibilityIcon />
      </Button>
      </Box>
      <Button
        variant="contained"
        onClick={handleDownload}
        sx={{
          backgroundColor: '#222',
          color: 'white',
          textTransform: 'none',
          '&:hover': {
            backgroundColor: '#444',
          },
        }}
      >
        <DownloadIcon />
      </Button>
    </div>
      {showQR && (
        <>
          {/* Overlay to close QR when clicking outside */}
          <div 
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0,0,0,0.5)',
              zIndex: 999
            }}
            onClick={() => setShowQR(false)}
          />
          <div style={{
            position: 'fixed',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            backgroundColor: 'white',
            padding: '1rem',
            boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
            borderRadius: '8px',
            zIndex: 1000
          }}>
            <QRCodeSVG value={url} size={256} />
          </div>
        </>
      )}
    </div>
  );
};
